import React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { Form, Formik, Field, FieldProps, FormikHelpers } from 'formik';
import { IconButton, MenuItem } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { NumericFormat } from 'react-number-format';

interface FormBuilder {
    fields: Array<any>;
    values?: any;
    initialValues?: any;
    disabled?: boolean;
    onSubmit?: (values: any, formikHelpers: FormikHelpers<any>) => void | Promise<any>;
    gap?: string;
    margin?: string;
    innerRef?: any;
    validationSchema?: any;    
}

const FormBuilder = ({ fields, disabled, values = {}, initialValues, onSubmit, gap, margin, innerRef, validationSchema }: FormBuilder) => {
    return (
        <Formik
            initialValues={{ ...(fields.reduce((o, f) => ({ ...o, [f.name]: null }))), ...initialValues }}
            onSubmit={onSubmit || ((e: any) => e)}
            validationSchema={validationSchema}
            {...(innerRef ? { innerRef } : {})}
        >
            {({ errors }) => (
                <Form className={`grid grid-cols-12 ${gap || 'gap-2 gap-x-3'} ${margin || 'my-4'}`}>
                    {FieldBuilder(fields, values, errors, disabled)}
                </Form>
            )}
        </Formik>
    );
};

const FieldBuilder = (fields: Array<any>, values: any, errors: any, formDisabled?: boolean) => {
    return (
        fields.map(f => (
            <React.Fragment key={f.name ?? `fragment-${f.name}`}>
                {['text', 'number', 'date', 'select'].includes(f.type) && (
                    <Field
                        className={`col-span-${f.col || 3}`}
                        key={f.name}
                        name={f.name}
                        as={TextField}
                        label={labelFormat(f)}
                        type={(f.type === 'select' ? 'text' : f.type) || 'text'}
                        error={Boolean(errors?.[f.name])}
                        disabled={formDisabled || f.disabled || false}
                        helperText={errors?.[f.name]}
                        InputLabelProps={{
                            ...((f.type === 'date' || values?.[f.name]) ? { shrink: true } : {}),
                            ...f.InputLabelProps
                        }}
                        {...(f.type === 'select' ? { select: true } : {})}
                        {...(values?.[f.name] ? { value: values?.[f.name] } : {})}
                        {...(f.onInput ? { onInput: f.onInput } : {})}
                        {...(f.multiline ? { multiline: true } : {})}
                        {...(f.multiline && f.rows ? { rows: f.rows } : {})}
                    >{
                            (f.type === 'select') && f.options?.map((item: any, index: number) => (
                                <MenuItem key={`${f.name}-${index}`} value={item.value}>{item.label}</MenuItem>
                            ))
                        }
                    </Field>
                )}
                {f.type === 'money' && (
                    <Field key={f.name} name={f.name}>
                        {({ field, form }: FieldProps) => (
                            <NumericFormat
                                className={`col-span-${f.col || 3}`}
                                customInput={TextField}
                                label={labelFormat(f)}
                                error={Boolean(errors?.[f.name])}
                                helperText={errors?.[f.name]}
                                InputLabelProps={values?.[f.name] ? { shrink: true } : {}}
                                value={field.value ?? ''}
                                thousandSeparator={f.thousandSeparator ?? '.'}
                                decimalSeparator={f.decimalSeparator ?? ','}
                                decimalScale={f.decimalScale ?? 2}
                                fixedDecimalScale={f.fixedDecimalScale ?? true}
                                allowNegative={f.allowNegative ?? false}
                                allowLeadingZeros={f.allowLeadingZeros ?? false}
                                prefix={f.prefix ?? 'R$ '}
                                onValueChange={(vals) => {
                                    const { floatValue } = vals;
                                    form.setFieldValue(f.name, Number.isFinite(floatValue as number) ? floatValue : null);
                                }}
                                onBlur={() => form.setFieldTouched(f.name, true, true)}
                            />
                        )}
                    </Field>
                )}

                {f.type === 'submit' && (
                    <Box key={f.type} className={'flex flex-center items-center'}>
                        <IconButton size={'large'} type={'submit'}>
                            {f.icon}
                        </IconButton>
                    </Box>
                )}
                {f.type === 'autocomplete' && (
                    <Field key={f.name} name={f.name}>
                        {({ form }: FieldProps) => (
                            <Autocomplete className={`col-span-${f.col || 3}`}
                                options={f.options}
                                {...(f.optionLabel ? { getOptionLabel: (option: any) => option[f.optionLabel] } : {})}
                                {...(f.optionKey ? { getOptionKey: (option: any) => option[f.optionKey] } : {})}
                                renderInput={(params) => <TextField {...params} label={f.label} error={Boolean(errors?.[f.name])} helperText={errors?.[f.name]} />}
                                onChange={(_, newValue) => { form.setFieldValue(f.name, newValue) }}
                                onBlur={() => form.setFieldTouched(f.name, true, true)}
                                {...(f.onInput ? { onInputChange: f.onInput } : {})}
                                filterOptions={(options) => options}
                                /*{(options, params) => {
                                    const input = params?.inputValue?.toLowerCase() || '';
                                    return options.filter((opt) => opt[f.name]?.toLowerCase().includes(input)) // add fields for filter
                                }}*/
                                isOptionEqualToValue={(option, value) =>
                                    f.optionKey ? option[f.optionKey] === value[f.optionKey] : option === value
                                }
                            />
                        )}
                    </Field>
                )}
                {f.type === 'group' && (
                    <Box key={f.type} className={`grid grid-cols-12 ${f.gap || 'gap-2 gap-x-3'} col-span-${f.col || 3}`}>
                        {FieldBuilder(f.fields, values, errors)}
                    </Box>
                )}
            </React.Fragment>
        ))
    )
}

const labelFormat = (f: any) => `${f.label}${f.required ? '*' : ''}`

export default FormBuilder;